import React, { useState, useEffect } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  CircularProgress,
  Button,
} from "@material-ui/core";
import DescriptionIcon from "@material-ui/icons/Description";
import axios from "axios";

export const STORAGE_KEY = "ai_approved_documents";

const ApprovedSources = () => {
  const [documents, setDocuments] = useState([]);
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setSelectedDocIds(Array.isArray(parsed) ? parsed : []);
      }
    } catch (err) {
      console.warn("[ApprovedSources] Failed to load saved selections:", err);
    }
  }, []);

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        setError("");

        const DOCUMENT_API = `${process.env.REACT_APP_BACKEND_URL}/documents`;
        const response = await axios.get(DOCUMENT_API);

        const allDocs = response?.data?.data?.documents || [];
        const approvedDocs = allDocs.filter((doc) => doc.status === "APPROVED");

        setDocuments(approvedDocs);
      } catch (err) {
        console.error("[ApprovedSources] Failed to fetch documents:", err);
        setError("Failed to load approved documents.");
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, []);

  const handleToggle = (documentId) => {
    setSelectedDocIds((prev) => {
      const newSelection = prev.includes(documentId)
        ? prev.filter((id) => id !== documentId)
        : [...prev, documentId];

      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newSelection));
      } catch (err) {
        console.warn("[ApprovedSources] Failed to save selections:", err);
      }

      return newSelection;
    });
  };

  const handleSelectAll = () => {
    const allIds = documents.map((doc) => doc.documentId);
    setSelectedDocIds(allIds);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(allIds));
    } catch (err) {
      console.warn("[ApprovedSources] Failed to save selections:", err);
    }
  };

  const handleClearAll = () => {
    setSelectedDocIds([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      console.warn("[ApprovedSources] Failed to clear selections:", err);
    }
  };

  if (loading) {
    return (
      <Box className="ai-tab-content" style={{ textAlign: "center", padding: "24px" }}>
        <CircularProgress size={32} />
        <Typography style={{ marginTop: "12px", color: "#666" }}>
          Loading approved documents...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="ai-tab-content" style={{ padding: "16px" }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (documents.length === 0) {
    return (
      <Box className="ai-tab-content" style={{ padding: "16px" }}>
        <Typography className="ai-section-note">
          No approved documents available. Please approve at least one document to use as a source.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="ai-tab-content" style={{ padding: "16px" }}>
      <Typography variant="subtitle2" style={{ marginBottom: "12px", fontWeight: 600 }}>
        Select Approved Sources ({selectedDocIds.length} selected)
      </Typography>

      <Typography className="ai-section-note" style={{ marginBottom: "16px" }}>
        Select approved documents to use as sources for Draft, Expand, and Generate actions.
      </Typography>

      <Box style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
        <Button
          size="small"
          variant="outlined"
          onClick={handleSelectAll}
          disabled={selectedDocIds.length === documents.length}
        >
          Select All
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={handleClearAll}
          disabled={selectedDocIds.length === 0}
        >
          Clear All
        </Button>
      </Box>

      <Box className="ai-approved-sources-list">
        {documents.map((doc) => (
          <Box
            key={doc.documentId}
            className="ai-approved-source-item"
            style={{
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
              padding: "12px",
              marginBottom: "8px",
              backgroundColor: selectedDocIds.includes(doc.documentId) ? "#f0f7ff" : "#fff",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedDocIds.includes(doc.documentId)}
                  onChange={() => handleToggle(doc.documentId)}
                  color="primary"
                  size="small"
                />
              }
              label={
                <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <DescriptionIcon style={{ fontSize: "18px", color: "#666" }} />
                  <Box>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {doc.procedureTitle || "Untitled Document"}
                    </Typography>
                    <Typography variant="caption" style={{ color: "#666" }}>
                      {doc.category || "No category"} • {doc.documentType || "Unknown type"}
                    </Typography>
                  </Box>
                </Box>
              }
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ApprovedSources;

export const getSelectedApprovedDocuments = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    console.log("[getSelectedApprovedDocuments] Raw from localStorage:", saved);
    if (saved) {
      const parsed = JSON.parse(saved);
      const result = Array.isArray(parsed) ? parsed : [];
      console.log("[getSelectedApprovedDocuments] Returning:", result);
      return result;
    }
  } catch (err) {
    console.error("[ApprovedSources] Failed to load selections:", err);
  }
  console.log("[getSelectedApprovedDocuments] Returning empty array");
  return [];
};
